// src/pages/Schedule/ScheduleBoard.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../lib/firebase";
import { collection, getDocs, doc, getDoc, query, where, deleteDoc } from "firebase/firestore";
import { readDays, saveDay } from "../../utils/scheduleStore";
import AdminPageHeader from "../../components/AdminPageHeader";
import SlotCard from "./components/SlotCard";
import TentCard from "./components/TentCard";
import MedicalHQCard from "./components/MedicalHQCard";
import CommandTeamCard from "./components/CommandTeamCard";
import DispatchCard from "./components/DispatchCard";
import Front9Card from "./components/Front9Card";
import Back9Card from "./components/Back9Card";
import { BLOCKS } from "./config/scheduleConfig";
import { buildDispatchRows } from "./builders/buildDispatchRows";
import { buildAssignmentGroups } from "./builders/buildAssignmentGroups";
import { buildMedicalHQRows } from "./builders/buildMedicalHQRows";
import { buildCommandTeamRows } from "./builders/buildCommandTeamRows";
import useScheduleDragDrop from "../../hooks/useScheduleDragDrop";
import useScheduleModals from "../../hooks/useScheduleModals";
import useWeekScheduleData from "../../hooks/useWeekScheduleData";
import useScheduleEmployees from "../../hooks/useScheduleEmployees";
import useTournamentWeek from "../../hooks/useTournamentWeek";
import { useNavigate } from "react-router-dom";
import {
  addDays,
  fmtDay,
  isISODate,
  prettyDate,
  startOfWeekMonday,
  ymd,
} from "../../utils/scheduleDateUtils";

import {
  getEmployeeName,
  getEmployeePosition,
  getEmployeeSearchText,
  isEmployeeAvailableForDate,
  isNewStaff,
  norm,
  prefersAidStation,
  prefersBikeTeam,
} from "../../utils/scheduleEmployeeUtils";

import {
  BACK_9_ASSIGNMENTS,
  COMMAND_TEAM_BASE_ROWS,
  DISPATCH_BASE_ROWS,
  FRONT_9_ASSIGNMENTS,
  MEDICAL_HQ_BASE_ROWS,
  buildReindexedExtraOrders,
  getCommandTeamBaseOrder,
  getCommandTeamExtraSortOrder,
  getCommandTeamNextExtraOrder,
  getDispatchBaseOrder,
  getDispatchExtraSortOrder,
  getDispatchNextExtraOrder,
  getInsideExtraSortOrder,
  getMedicalHQBaseOrder,
  getMedicalHQExtraSortOrder,
  getMedicalHQNextExtraOrder,
  getNextInsideExtraOrder,
} from "../../utils/scheduleOrderingUtils";

import {
  canHaveInsideExtras,
  getAddTargetsForBlock,
  isAmbulanceSlot,
  isOpsSupervisor,
  isTentSlot,
} from "../../utils/scheduleRoleUtils";

import {
  findBestOpenRange,
  hoursBetweenTimes,
  timeToMinutes,
} from "../../utils/scheduleTimeUtils";

import {
  ASSIGNMENTS_FIELD,
  AVAILABLE_SHIFTS_FIELD,
  BLOCKED_FIELD,
  EXTRA_SLOTS_FIELD,
  UNIT_TEXT_FIELD,
  getAddOptionsForBlock,
  getAutoPublishedShiftsForSlot,
  normalizeDay,
  serializeDay,
  uid,
} from "../../utils/scheduleDayUtils";

import {
  getEmployeeAssignmentsForDay,
  isQualifiedForPublishedPosition,
  isQualifiedForRole,
  validateSlotPlacement,
} from "../../utils/scheduleValidationUtils";

function formatRoleLabel(roleKey) {
  if (!roleKey) return "";

  if (/^Tent\s+\d+__\d+$/i.test(roleKey)) {
    const [tent, slot] = roleKey.split("__");
    return `${tent} - Slot ${slot === "1" ? "1 (Required)" : slot}`;
  }

  if (roleKey.includes("__")) {
    const [base, num] = roleKey.split("__");

    if (base === "Physician") return `Physician ${num}`;
    if (base === "RN") return `RN ${num}`;
    if (base === "APRN/PA") return `APRN/PA ${num}`;
    if (base === "Paramedic") return `Paramedic ${num}`;
    if (base === "EMT/AEMT") return `EMT/AEMT ${num}`;
    if (base.includes("Ambulance")) return `Ambulance ${num}`;

    return base;
  }

  return roleKey;
}

function getCanonicalRoleLabel(roleLabel = "") {
  const raw = String(roleLabel || "").trim();

  if (/^Physician(?:\s+|__)\d+$/i.test(raw)) return "Physician";
  if (/^APRN\/PA(?:\s+|__)\d+$/i.test(raw)) return "APRN/PA";
  if (/^RN(?:\s+|__)\d+$/i.test(raw)) return "RN";
  if (/^Paramedic(?:\s+|__)\d+$/i.test(raw)) return "Paramedic";
  if (/^EMT\/AEMT(?:\s+|__)\d+$/i.test(raw)) return "EMT/AEMT";
  if (/^Ambulance(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";
  if (/^Ambulance\s*\(ALS\)(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";
  if (/^Ambulance\s*\(BLS\)(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";

  return raw;
}

/* ---------------- modal ---------------- */
function Modal({ open, title, onClose, children, footer }) {
  if (!open) return null;

  return (
    <div className="sched-modal-backdrop" onMouseDown={onClose}>
      <div className="sched-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="sched-modal-header">
          <div className="sched-modal-title">{title}</div>
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="sched-modal-body">{children}</div>
        {footer ? <div className="sched-modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ---------------- main component ---------------- */
export default function ScheduleBoard({ readOnly = false }) {
  const canEdit = !readOnly;

  const {
    twLoading,
    twStartISO,
    twEndISO,
    initialWeekStart,
  } = useTournamentWeek({
    db,
    doc,
    getDoc,
    isISODate,
    startOfWeekMonday,
  });

  const [weekStart, setWeekStart] = useState(initialWeekStart);

  useEffect(() => {
    setWeekStart(initialWeekStart);
  }, [initialWeekStart]);

  const {
    employees,
    employeeById,
    setEmployees,
    setEmployeeById,
  } = useScheduleEmployees({
    db,
    collection,
    getDocs,
    getEmployeeName,
  });

  const {
    dragState,
    dragOverKey,
    setDragOverKey,
    handleDragStart,
    handleDragEnd,
    clearDragState,
    readDropPayload,
  } = useScheduleDragDrop();

  const tournamentLocked = !!twStartISO;

  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const displayWeekLabel = useMemo(() => {
    if (!weekDates.length) return "";
    return `${fmtDay(weekDates[0])} - ${fmtDay(weekDates[weekDates.length - 1])}`;
  }, [weekDates]);

  const navigate = useNavigate();

  const {
    weekDaysData,
    setWeekDaysData,
    loading,
    errMsg,
    commitDay,
  } = useWeekScheduleData({
    weekDates,
    ymd,
    normalizeDay,
    serializeDay,
  });

  /* ---------- derived ---------- */
  const roleMetaByDate = useMemo(() => {
    const out = {};
    for (const d of weekDates) {
      const iso = ymd(d);
      const day = normalizeDay(weekDaysData?.[iso] || {});
      const meta = new Map();

      for (const block of BLOCKS) {
        for (const slot of block.slots) {
          const isCart = /^Cart\s/i.test(slot);
          const isBike = /^Bike\s/i.test(slot);

          if (isCart || isBike) {
            meta.set(`${slot}__paramedic`, {
              roleKey: `${slot}__paramedic`,
              roleLabel: "Paramedic",
              displayLabel: "Paramedic",
              blockTitle: block.title,
              isExtraSlot: false,
            });

            meta.set(`${slot}__partner`, {
              roleKey: `${slot}__partner`,
              roleLabel: "Partner",
              displayLabel: "Partner",
              blockTitle: block.title,
              isExtraSlot: false,
            });
          } else {
            const canonicalRoleLabel =
              block.title === "Medical HQ"
                ? getCanonicalRoleLabel(slot)
                : slot;

            const displayLabel =
              block.title === "Medical HQ"
                ? formatRoleLabel(slot)
                : slot;

            meta.set(slot, {
              roleKey: slot,
              roleLabel: canonicalRoleLabel,
              displayLabel,
              blockTitle: block.title,
              isExtraSlot: false,
            });
          }
        }
      }

      for (const [blockTitle, extras] of Object.entries(day[EXTRA_SLOTS_FIELD] || {})) {
        if (!Array.isArray(extras)) continue;
        for (const extra of extras) {
          if (!extra?.id) continue;

          meta.set(extra.id, {
            roleKey: extra.id,
            roleLabel: extra.label || extra.roleType || extra.id,
            displayLabel: extra.label || extra.roleType || extra.id,
            blockTitle,
            isExtraSlot: true,
            insideParent: !!extra.insideParent,
            parentRoleKey: extra.roleKey || "",
          });
        }
      }

      out[iso] = meta;
    }
    return out;
  }, [weekDates, weekDaysData]);

  function getDay(iso) {
    return normalizeDay(weekDaysData?.[iso] || {});
  }

  function getEmployeeScheduledHoursForWeek(employeeId) {
    let total = 0;
    for (const d of weekDates) {
      const iso = ymd(d);
      const day = getDay(iso);
      const assignments = getEmployeeAssignmentsForDay(day, employeeId);
      assignments.forEach((a) => {
        total += hoursBetweenTimes(a.start, a.end);
      });
    }
    return total;
  }

  function getWeeklyRoleLabel(roleLabel) {
    const map = {
      "Incident Command": "Incident Cmd",
      "Unified Command": "Unified Cmd",
      "Triage Supervisor": "Triage Sup",
      "Ops Supervisor 1": "Ops Sup 1",
      "Ops Supervisor 2": "Ops Sup 2",
      "Ambulance (ALS)": "ALS Amb",
      Dispatcher: "Disp",
    };

    return map[roleLabel] || roleLabel;
  }

  /* ---------- slot helpers ---------- */
  function getAssignments(iso, roleKey) {
    const day = getDay(iso);
    return day[ASSIGNMENTS_FIELD]?.[roleKey] || [];
  }

  function isBlocked(iso, roleKey) {
    const day = getDay(iso);
    return !!day[BLOCKED_FIELD]?.[roleKey];
  }

  function getUnitText(iso, roleKey) {
    const day = getDay(iso);
    return String(day[UNIT_TEXT_FIELD]?.[roleKey] || "");
  }

  const {
    assignModal,
    setAssignModal,
    assignSearch,
    setAssignSearch,
    ambulanceModal,
    setAmbulanceModal,
    detailsModal,
    setDetailsModal,
    addPositionModal,
    setAddPositionModal,
    openAssignModalForSlot,
    openAssignModalForPublishedShift,
    openAmbulanceModal,
    openSlotInteraction,
  } = useScheduleModals({
    findBestOpenRange,
    getAssignments,
    getUnitText,
    isAmbulanceSlot,
    roleMetaByDate,
  });

  const assignModalSlotState = useMemo(() => {
    if (!assignModal) return null;

    const day = getDay(assignModal.iso);
    const assignments = day[ASSIGNMENTS_FIELD]?.[assignModal.roleKey] || [];
    const unitText = String(day[UNIT_TEXT_FIELD]?.[assignModal.roleKey] || "").trim();
    const storedAvailable = day[AVAILABLE_SHIFTS_FIELD]?.[assignModal.roleKey] || [];
    const blocked = !!day[BLOCKED_FIELD]?.[assignModal.roleKey];

    return {
      hasAssignments: assignments.length > 0,
      hasUnitText: !!unitText,
      hasStoredAvailable: storedAvailable.length > 0,
      blocked,
      canRemoveSlot:
        !!assignModal.isExtraSlot &&
        assignments.length === 0 &&
        !unitText &&
        storedAvailable.length === 0,
    };
  }, [assignModal, weekDaysData]);

 function getPublishedShiftsForSlot(iso, roleKey) {
  const meta = roleMetaByDate?.[iso]?.get(roleKey);
  if (!meta) return [];

  const roleLabel = meta.roleLabel;
  const blocked = isBlocked(iso, roleKey);
  const assignments = getAssignments(iso, roleKey).map((a) => ({
    ...a,
    employee: employeeById.get(a.employeeId),
  }));

  const day = getDay(iso);
  const availableMap = day[AVAILABLE_SHIFTS_FIELD] || {};

  const possibleKeys = Array.from(
    new Set([
      roleKey,
      String(roleKey || "").replace(/__/g, "_"),
      String(roleKey || "").replace(/_/g, "__"),
    ])
  );

  const storedAvailable = possibleKeys.flatMap((key) =>
    Array.isArray(availableMap[key]) ? availableMap[key] : []
  );

  const seen = new Set();
  const manual = storedAvailable
    .filter((shift) => {
      const sig = JSON.stringify({
        position: shift?.position || "",
        start: shift?.start || "",
        end: shift?.end || "",
        employeeId: shift?.employeeId || "",
      });
      if (seen.has(sig)) return false;
      seen.add(sig);
      return true;
    })
    .map((s) => ({
      ...s,
      iso,
      manual: true,
    }));

  // If this slot has someone assigned, it is NOT open.
// This also hides old/stale manual open shift records.
if (assignments.length > 0) {
  return [];
}

const auto = getAutoPublishedShiftsForSlot({
  roleKey,
  roleLabel,
  blocked,
  assignments,
}).map((s) => ({
  ...s,
  iso,
  manual: false,
}));

const source = manual.length > 0 ? manual : auto;

return source.filter((s) => {
  const key = `${s.start || ""}|${s.end || ""}|${s.position || ""}`;
  if (seen.has(key)) return false;
  seen.add(key);
  return true;
});
}

  function getInsideParentTentExtras(iso, roleKey, roleLabel) {
    if (!isTentSlot(roleLabel)) return [];

    const day = getDay(iso);
    const extras = Array.isArray(day[EXTRA_SLOTS_FIELD]?.[roleMetaByDate?.[iso]?.get(roleKey)?.blockTitle])
      ? day[EXTRA_SLOTS_FIELD][roleMetaByDate?.[iso]?.get(roleKey)?.blockTitle]
      : [];

    return extras.filter(
      (extra) =>
        extra?.insideParent &&
        extra?.roleKey === roleKey &&
        (extra?.label === roleLabel || extra?.roleType === roleLabel)
    );
  }

  function getInsideExtrasForRole(iso, blockTitle, parentRoleKey) {
    const day = getDay(iso);
    const extras = Array.isArray(day[EXTRA_SLOTS_FIELD]?.[blockTitle])
      ? day[EXTRA_SLOTS_FIELD][blockTitle]
      : [];

    return extras
      .filter((extra) => extra?.insideParent && extra?.roleKey === parentRoleKey)
      .sort((a, b) => getInsideExtraSortOrder(a) - getInsideExtraSortOrder(b));
  }

  async function saveAmbulanceUnit() {
    if (!ambulanceModal) return;
    await setUnitText(ambulanceModal.iso, ambulanceModal.roleKey, ambulanceModal.value || "");
    setAmbulanceModal(null);
  }

  async function setUnitText(iso, roleKey, value) {
    if (!canEdit) return;
    const nextDay = normalizeDay(getDay(iso));
    nextDay[UNIT_TEXT_FIELD] = {
      ...(nextDay[UNIT_TEXT_FIELD] || {}),
      [roleKey]: value,
    };
    await commitDay(iso, nextDay);
  }

  async function toggleBlocked(iso, roleKey) {
    if (!canEdit) return;

    const day = getDay(iso);
    const blocked = !!day[BLOCKED_FIELD]?.[roleKey];
    const currentAssignments = day[ASSIGNMENTS_FIELD]?.[roleKey] || [];
    const currentUnitText = String(day[UNIT_TEXT_FIELD]?.[roleKey] || "").trim();

    if (!blocked && (currentAssignments.length > 0 || currentUnitText)) {
      alert("Clear this slot before blocking it.");
      return;
    }

    const nextDay = normalizeDay(day);
    nextDay[BLOCKED_FIELD] = { ...(nextDay[BLOCKED_FIELD] || {}), [roleKey]: !blocked };
    await commitDay(iso, nextDay);
  }

  async function addExtraSlot({ iso, blockTitle, label, roleKey, insideParent }) {
    if (!canEdit) return;

    const nextDay = normalizeDay(getDay(iso));
    const slotId = uid(`EXTRA_${String(blockTitle || "Block").replace(/\s+/g, "_")}`);

    const list = Array.isArray(nextDay[EXTRA_SLOTS_FIELD]?.[blockTitle])
      ? nextDay[EXTRA_SLOTS_FIELD][blockTitle]
      : [];

    let order;
    if (insideParent && roleKey) {
      order = getNextInsideExtraOrder(
        list.filter((x) => x?.insideParent && x?.roleKey === roleKey)
      );
    } else if (blockTitle === "Medical HQ") {
      order = getMedicalHQNextExtraOrder(list);
    } else if (blockTitle === "Command Team") {
      order = getCommandTeamNextExtraOrder(list);
    } else if (blockTitle === "Dispatch") {
      order = getDispatchNextExtraOrder(list);
    }

    nextDay[EXTRA_SLOTS_FIELD] = {
      ...(nextDay[EXTRA_SLOTS_FIELD] || {}),
      [blockTitle]: [
        ...list,
        { id: slotId, roleType: label, label, roleKey, insideParent, ...(order != null ? { order } : {}) },
      ],
    };

    nextDay[ASSIGNMENTS_FIELD] = {
      ...(nextDay[ASSIGNMENTS_FIELD] || {}),
      [slotId]: [],
    };

    await commitDay(iso, nextDay);
  }

  async function removeExtraSlot({ iso, blockTitle, roleKey }) {
    if (!canEdit) return;

    const nextDay = normalizeDay(getDay(iso));
    const curAssignments = nextDay[ASSIGNMENTS_FIELD]?.[roleKey] || [];
    const curUnitText = String(nextDay[UNIT_TEXT_FIELD]?.[roleKey] || "").trim();
    const curPublished = nextDay[AVAILABLE_SHIFTS_FIELD]?.[roleKey] || [];

    if (curAssignments.length > 0 || curUnitText || curPublished.length > 0) {
      alert("Clear the slot before removing it.");
      return;
    }

    const list = Array.isArray(nextDay[EXTRA_SLOTS_FIELD]?.[blockTitle])
      ? nextDay[EXTRA_SLOTS_FIELD][blockTitle]
      : [];

    nextDay[EXTRA_SLOTS_FIELD] = {
      ...(nextDay[EXTRA_SLOTS_FIELD] || {}),
      [blockTitle]: list.filter((x) => x?.id !== roleKey),
    };

    const nextAssignments = { ...(nextDay[ASSIGNMENTS_FIELD] || {}) };
    delete nextAssignments[roleKey];
    nextDay[ASSIGNMENTS_FIELD] = nextAssignments;

    const blockedBag = { ...(nextDay[BLOCKED_FIELD] || {}) };
    delete blockedBag[roleKey];
    nextDay[BLOCKED_FIELD] = blockedBag;

    const unitTextBag = { ...(nextDay[UNIT_TEXT_FIELD] || {}) };
    delete unitTextBag[roleKey];
    nextDay[UNIT_TEXT_FIELD] = unitTextBag;

    const availBag = { ...(nextDay[AVAILABLE_SHIFTS_FIELD] || {}) };
    delete availBag[roleKey];
    nextDay[AVAILABLE_SHIFTS_FIELD] = availBag;

    await commitDay(iso, nextDay);
  }

  async function reorderMedicalHQExtraSlot({ iso, sourceRoleKey, targetRoleKey, placement }) {
    if (!canEdit) return;
    if (!sourceRoleKey || !targetRoleKey || sourceRoleKey === targetRoleKey) return;

    const nextDay = normalizeDay(getDay(iso));
    const extras = Array.isArray(nextDay[EXTRA_SLOTS_FIELD]?.["Medical HQ"])
      ? [...nextDay[EXTRA_SLOTS_FIELD]["Medical HQ"]]
      : [];

    const topLevelExtras = extras.filter((x) => !x?.insideParent);
    const sourceExtra = topLevelExtras.find((x) => x?.id === sourceRoleKey);
    if (!sourceExtra) return;

    const currentSequence = [
      ...MEDICAL_HQ_BASE_ROWS.map((roleKey) => ({
        key: roleKey,
        order: getMedicalHQBaseOrder(roleKey),
        isBase: true,
      })),
      ...topLevelExtras.map((extra) => ({
        key: extra.id,
        order: getMedicalHQExtraSortOrder(extra),
        isBase: false,
      })),
    ]
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        if (a.isBase !== b.isBase) return a.isBase ? -1 : 1;
        return String(a.key).localeCompare(String(b.key));
      })
      .map((x) => x.key);

    const withoutSource = currentSequence.filter((key) => key !== sourceRoleKey);
    const targetIndex = withoutSource.findIndex((key) => key === targetRoleKey);
    if (targetIndex === -1) return;

    const insertAt = placement === "after" ? targetIndex + 1 : targetIndex;
    const nextSequence = [...withoutSource];
    nextSequence.splice(insertAt, 0, sourceRoleKey);

    const nextOrders = buildReindexedExtraOrders(
      nextSequence,
      MEDICAL_HQ_BASE_ROWS,
      getMedicalHQBaseOrder
    );

    nextDay[EXTRA_SLOTS_FIELD] = {
      ...(nextDay[EXTRA_SLOTS_FIELD] || {}),
      "Medical HQ": extras.map((extra) =>
        extra?.id && nextOrders[extra.id] != null
          ? { ...extra, order: nextOrders[extra.id] }
          : extra
      ),
    };

    await commitDay(iso, nextDay);
  }

  async function reorderCommandTeamExtraSlot({ iso, sourceRoleKey, targetRoleKey, placement }) {
    if (!canEdit) return;
    if (!sourceRoleKey || !targetRoleKey || sourceRoleKey === targetRoleKey) return;

    const nextDay = normalizeDay(getDay(iso));
    const extras = Array.isArray(nextDay[EXTRA_SLOTS_FIELD]?.["Command Team"])
      ? [...nextDay[EXTRA_SLOTS_FIELD]["Command Team"]]
      : [];

    const sourceExtra = extras.find((x) => x?.id === sourceRoleKey && !x?.insideParent);
    if (!sourceExtra) return;

    const otherExtras = extras.filter((x) => x?.id !== sourceRoleKey && !x?.insideParent);

    const entries = [
      ...COMMAND_TEAM_BASE_ROWS.map((roleKey) => ({
        roleKey,
        order: getCommandTeamBaseOrder(roleKey),
        isExtra: false,
      })),
      ...otherExtras.map((extra) => ({
        roleKey: extra.id,
        order: getCommandTeamExtraSortOrder(extra),
        isExtra: true,
      })),
    ].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if (a.isExtra !== b.isExtra) return a.isExtra ? 1 : -1;
      return String(a.roleKey).localeCompare(String(b.roleKey));
    });

    const targetIndex = entries.findIndex((x) => x.roleKey === targetRoleKey);
    if (targetIndex === -1) return;

    const insertIndex = placement === "after" ? targetIndex + 1 : targetIndex;
    const prev = entries[insertIndex - 1] || null;
    const next = entries[insertIndex] || null;

    let newOrder;
    if (!prev && !next) {
      newOrder = 550;
    } else if (!prev) {
      newOrder = next.order - 50;
    } else if (!next) {
      newOrder = prev.order + 50;
    } else {
      newOrder = (prev.order + next.order) / 2;
    }

    nextDay[EXTRA_SLOTS_FIELD] = {
      ...(nextDay[EXTRA_SLOTS_FIELD] || {}),
      "Command Team": extras.map((extra) =>
        extra?.id === sourceRoleKey ? { ...extra, order: newOrder } : extra
      ),
    };

    await commitDay(iso, nextDay);
  }

  async function reorderDispatchExtraSlot({ iso, sourceRoleKey, targetRoleKey, placement }) {
    if (!canEdit) return;
    if (!sourceRoleKey || !targetRoleKey || sourceRoleKey === targetRoleKey) return;

    const nextDay = normalizeDay(getDay(iso));
    const extras = Array.isArray(nextDay[EXTRA_SLOTS_FIELD]?.["Dispatch"])
      ? [...nextDay[EXTRA_SLOTS_FIELD]["Dispatch"]]
      : [];

    const sourceExtra = extras.find((x) => x?.id === sourceRoleKey && !x?.insideParent);
    if (!sourceExtra) return;

    const otherExtras = extras.filter((x) => x?.id !== sourceRoleKey && !x?.insideParent);

    const entries = [
      ...DISPATCH_BASE_ROWS.map((roleKey) => ({
        roleKey,
        order: getDispatchBaseOrder(roleKey),
        isExtra: false,
      })),
      ...otherExtras.map((extra) => ({
        roleKey: extra.id,
        order: getDispatchExtraSortOrder(extra),
        isExtra: true,
      })),
    ].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if (a.isExtra !== b.isExtra) return a.isExtra ? 1 : -1;
      return String(a.roleKey).localeCompare(String(b.roleKey));
    });

    const targetIndex = entries.findIndex((x) => x.roleKey === targetRoleKey);
    if (targetIndex === -1) return;

    const insertIndex = placement === "after" ? targetIndex + 1 : targetIndex;
    const prev = entries[insertIndex - 1] || null;
    const next = entries[insertIndex] || null;

    let newOrder;
    if (!prev && !next) {
      newOrder = 150;
    } else if (!prev) {
      newOrder = next.order - 50;
    } else if (!next) {
      newOrder = prev.order + 50;
    } else {
      newOrder = (prev.order + next.order) / 2;
    }

    nextDay[EXTRA_SLOTS_FIELD] = {
      ...(nextDay[EXTRA_SLOTS_FIELD] || {}),
      Dispatch: extras.map((extra) =>
        extra?.id === sourceRoleKey ? { ...extra, order: newOrder } : extra
      ),
    };

    await commitDay(iso, nextDay);
  }

  async function reorderInsideAssignmentExtraSlot({
    iso,
    blockTitle,
    parentRoleKey,
    sourceRoleKey,
    targetRoleKey,
    placement,
  }) {
    if (!canEdit) return;
    if (!sourceRoleKey || !targetRoleKey || sourceRoleKey === targetRoleKey) return;

    const nextDay = normalizeDay(getDay(iso));
    const extras = Array.isArray(nextDay[EXTRA_SLOTS_FIELD]?.[blockTitle])
      ? [...nextDay[EXTRA_SLOTS_FIELD][blockTitle]]
      : [];

    const relevant = extras.filter(
      (x) => x?.insideParent && x?.roleKey === parentRoleKey
    );

    const sourceExtra = relevant.find((x) => x?.id === sourceRoleKey);
    if (!sourceExtra) return;

    const otherExtras = relevant.filter((x) => x?.id !== sourceRoleKey);

    const entries = [
      { roleKey: parentRoleKey, order: 100, isExtra: false },
      ...otherExtras.map((extra) => ({
        roleKey: extra.id,
        order: getInsideExtraSortOrder(extra),
        isExtra: true,
      })),
    ].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if (a.isExtra !== b.isExtra) return a.isExtra ? 1 : -1;
      return String(a.roleKey).localeCompare(String(b.roleKey));
    });

    const targetIndex = entries.findIndex((x) => x.roleKey === targetRoleKey);
    if (targetIndex === -1) return;

    const insertIndex = placement === "after" ? targetIndex + 1 : targetIndex;
    const prev = entries[insertIndex - 1] || null;
    const next = entries[insertIndex] || null;

    let newOrder;
    if (!prev && !next) {
      newOrder = 150;
    } else if (!prev) {
      newOrder = next.order - 50;
    } else if (!next) {
      newOrder = prev.order + 50;
    } else {
      newOrder = (prev.order + next.order) / 2;
    }

    nextDay[EXTRA_SLOTS_FIELD] = {
      ...(nextDay[EXTRA_SLOTS_FIELD] || {}),
      [blockTitle]: extras.map((extra) =>
        extra?.id === sourceRoleKey ? { ...extra, order: newOrder } : extra
      ),
    };

    await commitDay(iso, nextDay);
  }

  async function assignEmployeeToSlot({ iso, roleKey, employeeId, start, end }) {
    if (!canEdit) return;

    const day = getDay(iso);
    const meta = roleMetaByDate?.[iso]?.get(roleKey);
    if (!meta) return;

    if (isBlocked(iso, roleKey)) {
      alert("This slot is blocked.");
      return;
    }

    if (isAmbulanceSlot(meta.roleLabel)) {
      alert("Ambulance slots use free-text unit names, not employee assignments.");
      return;
    }

    const employee = employeeById.get(employeeId);
    if (!employee) {
      alert("Employee not found.");
      return;
    }

    const check = validateSlotPlacement({
      day,
      roleKey,
      roleLabel: meta.roleLabel,
      employee,
      employeeById,
      start,
      end,
    });

    if (!check.ok) {
      alert(check.message);
      return;
    }

    const nextDay = normalizeDay(day);
    const list = nextDay[ASSIGNMENTS_FIELD]?.[roleKey] || [];
    nextDay[ASSIGNMENTS_FIELD] = {
      ...(nextDay[ASSIGNMENTS_FIELD] || {}),
      [roleKey]: [...list, { assignmentId: uid("asg"), employeeId, start, end }],
    };

    await commitDay(iso, nextDay);
  }

  async function updateAssignment({ iso, roleKey, assignmentId, start, end, targetRoleKey = roleKey }) {
    if (!canEdit) return;

    const day = getDay(iso);
    const targetMeta = roleMetaByDate?.[iso]?.get(targetRoleKey);
    if (!targetMeta) return;

    if (isAmbulanceSlot(targetMeta.roleLabel)) {
      alert("You cannot drag employees into ambulance slots.");
      return;
    }

    const currentList = getAssignments(iso, roleKey);
    const current = currentList.find((a) => a.assignmentId === assignmentId);
    if (!current) return;

    const employee = employeeById.get(current.employeeId);
    if (!employee) {
      alert("Employee not found.");
      return;
    }

    if (isBlocked(iso, targetRoleKey)) {
      alert("That destination slot is blocked.");
      return;
    }

    const check = validateSlotPlacement({
      day,
      roleKey: targetRoleKey,
      roleLabel: targetMeta.roleLabel,
      employee,
      employeeById,
      start,
      end,
      excludeAssignmentId: assignmentId,
    });

    if (!check.ok) {
      alert(check.message);
      return;
    }

    const nextDay = normalizeDay(day);
    nextDay[ASSIGNMENTS_FIELD] = {
      ...(nextDay[ASSIGNMENTS_FIELD] || {}),
      [roleKey]: (nextDay[ASSIGNMENTS_FIELD]?.[roleKey] || []).filter((a) => a.assignmentId !== assignmentId),
    };

    const targetList = nextDay[ASSIGNMENTS_FIELD]?.[targetRoleKey] || [];
    nextDay[ASSIGNMENTS_FIELD][targetRoleKey] = [
      ...targetList,
      {
        assignmentId,
        employeeId: current.employeeId,
        start,
        end,
      },
    ];

    await commitDay(iso, nextDay);
  }

 async function removeAssignment({ iso, roleKey, assignmentId }) {
  if (!canEdit) return;

  const nextDay = normalizeDay(getDay(iso));

  const originalList = nextDay[ASSIGNMENTS_FIELD]?.[roleKey] || [];
  const removedAssignment = originalList.find((a) => a.assignmentId === assignmentId);

  const updatedAssignments = originalList.filter(
    (a) => a.assignmentId !== assignmentId
  );

  nextDay[ASSIGNMENTS_FIELD] = {
    ...(nextDay[ASSIGNMENTS_FIELD] || {}),
    [roleKey]: updatedAssignments,
  };

  if (updatedAssignments.length === 0) {
    const blockedBag = { ...(nextDay[BLOCKED_FIELD] || {}) };

    if (blockedBag[roleKey]) {
      delete blockedBag[roleKey];
      nextDay[BLOCKED_FIELD] = blockedBag;
    }

    const meta = roleMetaByDate?.[iso]?.get(roleKey);
    const roleLabel = meta?.roleLabel || roleKey;

    const start = removedAssignment?.start || "06:00";
    const end = removedAssignment?.end || "19:00";

    const autoOpen = getAutoPublishedShiftsForSlot({
      roleKey,
      roleLabel,
      blocked: false,
      assignments: [],
    });

    if (autoOpen.length > 0) {
      const existingAvailable = Array.isArray(nextDay[AVAILABLE_SHIFTS_FIELD]?.[roleKey])
        ? nextDay[AVAILABLE_SHIFTS_FIELD][roleKey]
        : [];

      const alreadyExists = existingAvailable.some((s) => {
        return String(s.start || "") === String(start) && String(s.end || "") === String(end);
      });

      if (!alreadyExists) {
        nextDay[AVAILABLE_SHIFTS_FIELD] = {
          ...(nextDay[AVAILABLE_SHIFTS_FIELD] || {}),
          [roleKey]: [
            ...existingAvailable,
            {
              ...autoOpen[0],
              id: `reopened_${iso}_${roleKey}_${start}_${end}`,
              roleKey,
              roleLabel,
              start,
              end,
              manual: true,
              reopenedFromManualRemoval: true,
              reopenedAtMs: Date.now(),
            },
          ],
        };
      }
    }

    try {
      const claimsRef = collection(db, "shiftClaims");
      const q = query(
        claimsRef,
        where("dateId", "==", iso),
        where("roleKey", "==", roleKey),
        where("status", "==", "awarded")
      );

      const snap = await getDocs(q);

      for (const claimDoc of snap.docs) {
        await deleteDoc(claimDoc.ref);
      }

      console.log("Cleared awarded shiftClaims for reopened slot:", roleKey);
    } catch (err) {
      console.error("Failed to clean shiftClaims:", err);
    }
  }

  await commitDay(iso, nextDay);
}

  /* ---------- modal data ---------- */
  const assignCandidates = useMemo(() => {
    if (!assignModal) return [];

    const { iso, roleKey, start, end, publishedPosition = "" } = assignModal;
    const day = getDay(iso);
    const meta = roleMetaByDate?.[iso]?.get(roleKey);
    if (!meta || isAmbulanceSlot(meta.roleLabel)) return [];

    const q = norm(assignSearch);
    const isTentContext = isTentSlot(meta.roleLabel);
    const isBikeContext = /^Bike\s/i.test(String(meta.roleLabel || ""));

    return employees
      .filter((emp) => {
  

  // DO NOT FILTER OUT UNAVAILABLE STAFF
  // We want admins to still see them for override scheduling

  if (!q) return true;

  const hay =
    `${getEmployeeName(emp)} ${emp.email || ""} ${getEmployeeSearchText(emp)}`
      .toLowerCase();

  return hay.includes(q);
})
      .map((emp) => {
        const dayAssignments = getEmployeeAssignmentsForDay(day, emp.id);
        const availableForDay = isEmployeeAvailableForDate(emp, iso);

        const placementCheck = validateSlotPlacement({
          day,
          roleKey,
          roleLabel: meta.roleLabel,
          employee: emp,
          employeeById,
          start,
          end,
        });

        const alreadyAssignedToday = dayAssignments.length > 0;
        const assignmentSummary = dayAssignments.map((a) => `${a.roleKey} (${a.start}-${a.end})`).join(", ");

        let statusLabel = "Available";
let reason = "";
let canAssign = placementCheck.ok;

if (!availableForDay && canAssign) {
  statusLabel = "Availability Override";
  reason = "Employee is not marked available for this day.";
}

if (!placementCheck.ok) {
  canAssign = false;

  if (alreadyAssignedToday) {
    statusLabel = "Already Assigned";
    reason = assignmentSummary
      ? `Currently assigned: ${assignmentSummary}`
      : placementCheck.message;
  } else {
    statusLabel = "Unavailable";
    reason = placementCheck.message;
  }
}

        return {
          emp,
          canAssign,
          availableForDay,
          statusLabel,
          reason,
          weekHours: getEmployeeScheduledHoursForWeek(emp.id),
          tentPreferred: isTentContext ? prefersAidStation(emp) : false,
          bikePreferred: isBikeContext ? prefersBikeTeam(emp) : false,
        };
      })
      .sort((a, b) => {
        if (a.canAssign !== b.canAssign) return a.canAssign ? -1 : 1;

        if (isTentContext && a.tentPreferred !== b.tentPreferred) {
          return a.tentPreferred ? -1 : 1;
        }

        if (isBikeContext && a.bikePreferred !== b.bikePreferred) {
          return a.bikePreferred ? -1 : 1;
        }

        return String(getEmployeeName(a.emp)).localeCompare(String(getEmployeeName(b.emp)));
      });
  }, [
    assignModal,
    assignSearch,
    employees,
    employeeById,
    roleMetaByDate,
    weekDaysData,
    weekDates,
  ]);

  function reorderAssignments(list, sourceAssignmentId, targetAssignmentId, placement = "before") {
    const items = [...list];
    const sourceIndex = items.findIndex((a) => a.assignmentId === sourceAssignmentId);
    const targetIndex = items.findIndex((a) => a.assignmentId === targetAssignmentId);

    if (sourceIndex === -1 || targetIndex === -1) return list;
    if (sourceIndex === targetIndex) return list;

    const [moved] = items.splice(sourceIndex, 1);

    const adjustedTargetIndex =
      sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;

    const insertIndex =
      placement === "after" ? adjustedTargetIndex + 1 : adjustedTargetIndex;

    items.splice(insertIndex, 0, moved);
    return items;
  }

  async function handleDropOnAssignmentCard(
    e,
    targetIso,
    targetRoleKey,
    targetAssignmentId,
    placement = "before"
  ) {
    if (!canEdit) return;
    setDragOverKey("");

    const payload = readDropPayload(e);
    if (!payload) return;

    if (payload.iso !== targetIso) {
      alert("Assignments can only be dragged within the same day.");
      return;
    }

    if (payload.roleKey !== targetRoleKey) {
      await updateAssignment({
        iso: payload.iso,
        roleKey: payload.roleKey,
        assignmentId: payload.assignmentId,
        start: payload.start,
        end: payload.end,
        targetRoleKey,
      });
      clearDragState();
      return;
    }

    if (payload.assignmentId === targetAssignmentId) {
      clearDragState();
      return;
    }

    const nextDay = normalizeDay(getDay(targetIso));
    const currentList = nextDay[ASSIGNMENTS_FIELD]?.[targetRoleKey] || [];

    nextDay[ASSIGNMENTS_FIELD] = {
      ...(nextDay[ASSIGNMENTS_FIELD] || {}),
      [targetRoleKey]: reorderAssignments(
        currentList,
        payload.assignmentId,
        targetAssignmentId,
        placement
      ),
    };

    await commitDay(targetIso, nextDay);
    clearDragState();
  }

  async function handleDropOnSlot(e, targetIso, targetRoleKey) {
    if (!canEdit) return;
    setDragOverKey("");

    const payload = readDropPayload(e);
    if (!payload) return;

    if (payload.iso !== targetIso) {
      alert("Assignments can only be dragged within the same day.");
      return;
    }

    await updateAssignment({
      iso: payload.iso,
      roleKey: payload.roleKey,
      assignmentId: payload.assignmentId,
      start: payload.start,
      end: payload.end,
      targetRoleKey,
    });

    clearDragState();
  }

  function makeRowHandlers(iso, roleKey, roleLabel, blockTitle) {
    return {
      onOpenAssign: () =>
        openSlotInteraction({
          iso,
          roleKey,
          roleLabel,
          blockTitle,
          publishedPosition: "",
        }),

      onPublishedShiftClick: (shift) =>
        openAssignModalForPublishedShift({ ...shift, iso }, blockTitle),

      onEditAssignment: (assignment) => {
        setDetailsModal({
          iso,
          roleKey: assignment.roleKey || roleKey,
          assignmentId: assignment.assignmentId,
          employeeId: assignment.employeeId,
          roleLabel,
          start: assignment.start,
          end: assignment.end,
        });
      },

      onToggleBlocked: () => toggleBlocked(iso, roleKey),

      onDropAssignment: (e) => handleDropOnSlot(e, iso, roleKey),

      onDragStart: (e, assignment) =>
        handleDragStart(e, {
          iso,
          roleKey,
          assignmentId: assignment.assignmentId,
          employeeId: assignment.employeeId,
          start: assignment.start,
          end: assignment.end,
        }),

      onDragEnd: handleDragEnd,
      onDragEnter: () => setDragOverKey(`${iso}__${roleKey}`),
      onDragLeave: () =>
        setDragOverKey((cur) => (cur === `${iso}__${roleKey}` ? "" : cur)),
    };
  }

  return (
    <div className="admin-schedule view-weekly">
      <div className="top-bar">
        <div className="controls-left controls-left-stacked" style={{ width: "100%" }}>
          <AdminPageHeader title="Schedule" />

          <div className="schedule-week-line">
            <div className="schedule-week-label">
              {tournamentLocked ? "Tournament Week" : "Week"}
            </div>
            <div className="schedule-week-value">{displayWeekLabel}</div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => navigate("/admin/schedule/employees")}
            >
              Employee Weekly View
            </button>
          </div>
        </div>
      </div>

      {(loading || twLoading) && <div style={{ padding: 12 }}>Loading…</div>}

      {!!errMsg && (
        <div style={{ padding: 12, color: "#991b1b", background: "#fff1f2", borderRadius: 12 }}>
          {errMsg}
        </div>
      )}

      {!loading && !twLoading && !errMsg && (
        <div className="schedule-container schedule-container-full">
          <div className="schedule-table">
            <div className="schedule-dates-row">
              {weekDates.map((d) => {
                const iso = ymd(d);
                return (
                  <div key={iso} className="schedule-date-cell">
                    <div className="day-header-pill">{fmtDay(d)}</div>
                  </div>
                );
              })}
            </div>

            <div className="schedule-body-scroll">
              <div className="schedule-row">
                {weekDates.map((d) => {
                  const iso = ymd(d);
                  const day = getDay(iso);

                  return (
                    <div key={iso} className="schedule-day">
                      <div className="day-body">
                        {BLOCKS.map((block) => {
                          const extras = Array.isArray(day[EXTRA_SLOTS_FIELD]?.[block.title])
                            ? day[EXTRA_SLOTS_FIELD][block.title]
                            : [];
                          const addOptions = getAddOptionsForBlock(block.title);

                          return (
                            <div key={block.title} className="assignment-subblock">
                              <div className="subblock-header">
                                <h4 className="assignment-title">{block.title}</h4>

                                {canEdit && addOptions.length > 0 && (
                                  <div className="schedule-add-position-wrap">
                                    <button
                                      className="btn btn-ghost schedule-add-position-btn"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAddPositionModal({
                                          iso,
                                          blockTitle: block.title,
                                          options: addOptions,
                                          selected: addOptions[0] || "",
                                          targetRoleKey:
                                            getAddTargetsForBlock(
                                              block.title,
                                              FRONT_9_ASSIGNMENTS,
                                              BACK_9_ASSIGNMENTS
                                            )[0] || "",
                                          targetOptions: getAddTargetsForBlock(
                                            block.title,
                                            FRONT_9_ASSIGNMENTS,
                                            BACK_9_ASSIGNMENTS
                                          ),
                                        });
                                      }}
                                    >
                                      + Add Position
                                    </button>
                                  </div>
                                )}
                              </div>

                              <div style={{ display: "grid", gap: 8 }}>
                                {block.title === "Command Team" ? (
                                  <CommandTeamCard
                                    iso={iso}
                                    employeeById={employeeById}
                                    dragOverKey={dragOverKey}
                                    readOnly={!canEdit}
                                    onReorderExtraRow={({ sourceRoleKey, targetRoleKey, placement }) =>
                                      reorderCommandTeamExtraSlot({
                                        iso,
                                        sourceRoleKey,
                                        targetRoleKey,
                                        placement,
                                      })
                                    }
                                    rows={buildCommandTeamRows({
                                      rows: (() => {
                                        const commandExtras = extras
                                          .filter((extra) => {
                                            const extraMeta = roleMetaByDate?.[iso]?.get(extra.id);
                                            return !!extraMeta;
                                          })
                                          .map((extra) => ({
                                            roleKey: extra.id,
                                            sortOrder: getCommandTeamExtraSortOrder(extra),
                                          }));

                                        const orderedRowKeys = [
                                          ...COMMAND_TEAM_BASE_ROWS.map((roleKey) => ({
                                            roleKey,
                                            sortOrder: getCommandTeamBaseOrder(roleKey),
                                          })),
                                          ...commandExtras,
                                        ]
                                          .sort((a, b) => {
                                            if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
                                            const aIsBase = COMMAND_TEAM_BASE_ROWS.includes(a.roleKey);
                                            const bIsBase = COMMAND_TEAM_BASE_ROWS.includes(b.roleKey);
                                            if (aIsBase !== bIsBase) return aIsBase ? -1 : 1;
                                            return String(a.roleKey).localeCompare(String(b.roleKey));
                                          })
                                          .map((x) => x.roleKey);

                                        return orderedRowKeys.map((rowKey) => {
                                          const isBaseRole = COMMAND_TEAM_BASE_ROWS.includes(rowKey);

                                          const rowMeta = isBaseRole
                                            ? roleMetaByDate?.[iso]?.get(rowKey) || {
                                                roleKey: rowKey,
                                                roleLabel: rowKey,
                                                blockTitle: block.title,
                                                isExtraSlot: false,
                                              }
                                            : roleMetaByDate?.[iso]?.get(rowKey);

                                          const rowRoleLabel = rowMeta?.roleLabel || rowKey;
                                          const rowAssignments = getAssignments(iso, rowKey);

                                          return {
                                            roleKey: rowKey,
                                            roleLabel: rowRoleLabel,
                                            displayLabel: getWeeklyRoleLabel(rowMeta?.displayLabel || rowRoleLabel),
                                            assignment: rowAssignments[0] || null,
                                            assignments: rowAssignments,
                                            blocked: isBlocked(iso, rowKey),
                                            dragOver: dragOverKey === `${iso}__${rowKey}`,
                                            publishedShifts: getPublishedShiftsForSlot(iso, rowKey),
                                            isExtraSlot: !!rowMeta?.isExtraSlot,
                                          };
                                        });
                                      })(),
                                      assignmentsByRole: {},
                                      blockedSlots: {},
                                      publishedShiftsByRole: {},
                                      handlers: (rowKey) => {
                                        const rowMeta = roleMetaByDate?.[iso]?.get(rowKey) || {
                                          roleKey: rowKey,
                                          roleLabel: rowKey,
                                          blockTitle: block.title,
                                          isExtraSlot: false,
                                        };
                                        const rowRoleLabel = rowMeta?.roleLabel || rowKey;

                                        return makeRowHandlers(iso, rowKey, rowRoleLabel, block.title);
                                      },
                                    })}
                                  />
                                ) : block.title === "Dispatch" ? (
                                  <DispatchCard
                                    iso={iso}
                                    employeeById={employeeById}
                                    dragOverKey={dragOverKey}
                                    readOnly={!canEdit}
                                    onReorderExtraRow={({ sourceRoleKey, targetRoleKey, placement }) =>
                                      reorderDispatchExtraSlot({
                                        iso,
                                        sourceRoleKey,
                                        targetRoleKey,
                                        placement,
                                      })
                                    }
                                    rows={buildDispatchRows({
                                      rows: (() => {
                                        const dispatchExtras = extras
                                          .filter((extra) => {
                                            const extraMeta = roleMetaByDate?.[iso]?.get(extra.id);
                                            return !!extraMeta;
                                          })
                                          .map((extra) => ({
                                            roleKey: extra.id,
                                            sortOrder: getDispatchExtraSortOrder(extra),
                                          }));

                                        const orderedRowKeys = [
                                          ...DISPATCH_BASE_ROWS.map((roleKey) => ({
                                            roleKey,
                                            sortOrder: getDispatchBaseOrder(roleKey),
                                          })),
                                          ...dispatchExtras,
                                        ]
                                          .sort((a, b) => {
                                            if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
                                            const aIsBase = DISPATCH_BASE_ROWS.includes(a.roleKey);
                                            const bIsBase = DISPATCH_BASE_ROWS.includes(b.roleKey);
                                            if (aIsBase !== bIsBase) return aIsBase ? -1 : 1;
                                            return String(a.roleKey).localeCompare(String(b.roleKey));
                                          })
                                          .map((x) => x.roleKey);

                                        return orderedRowKeys.map((rowKey) => {
                                          const isBaseRole = DISPATCH_BASE_ROWS.includes(rowKey);

                                          const rowMeta = isBaseRole
                                            ? roleMetaByDate?.[iso]?.get(rowKey) || {
                                                roleKey: rowKey,
                                                roleLabel: rowKey,
                                                blockTitle: block.title,
                                                isExtraSlot: false,
                                              }
                                            : roleMetaByDate?.[iso]?.get(rowKey);

                                          const rowRoleLabel = rowMeta?.roleLabel || rowKey;
                                          const rowAssignments = getAssignments(iso, rowKey);

                                          return {
                                            roleKey: rowKey,
                                            roleLabel: rowRoleLabel,
                                            displayLabel: getWeeklyRoleLabel(rowMeta?.displayLabel || rowRoleLabel),
                                            assignment: rowAssignments[0] || null,
                                            assignments: rowAssignments,
                                            blocked: isBlocked(iso, rowKey),
                                            dragOver: dragOverKey === `${iso}__${rowKey}`,
                                            publishedShifts: getPublishedShiftsForSlot(iso, rowKey),
                                            isExtraSlot: !!rowMeta?.isExtraSlot,
                                          };
                                        });
                                      })(),
                                      assignmentsByRole: {},
                                      blockedSlots: {},
                                      publishedShiftsByRole: {},
                                      extras: [],
                                      handlers: (rowKey) => {
                                        const rowMeta = roleMetaByDate?.[iso]?.get(rowKey) || {
                                          roleKey: rowKey,
                                          roleLabel: rowKey,
                                          blockTitle: block.title,
                                          isExtraSlot: false,
                                        };
                                        const rowRoleLabel = rowMeta?.roleLabel || rowKey;
                                        return makeRowHandlers(iso, rowKey, rowRoleLabel, block.title);
                                      },
                                    })}
                                  />
                                ) : block.title === "Front 9" ? (
                                  <Front9Card
                                    iso={iso}
                                    employeeById={employeeById}
                                    dragOverKey={dragOverKey}
                                    readOnly={!canEdit}
                                    groups={buildAssignmentGroups({
                                      groups: FRONT_9_ASSIGNMENTS.map((assignmentRoleKey) => {
                                        const meta = roleMetaByDate?.[iso]?.get(assignmentRoleKey) || {
                                          roleKey: assignmentRoleKey,
                                          roleLabel: assignmentRoleKey,
                                          blockTitle: block.title,
                                          isExtraSlot: false,
                                        };

                                        const roleLabel = meta.roleLabel;
                                        const isTentBase = /^Tent\s+\d+$/i.test(String(assignmentRoleKey || ""));
                                        const extraDefs =
                                          canHaveInsideExtras(roleLabel) && !isTentBase
                                            ? getInsideExtrasForRole(iso, block.title, assignmentRoleKey)
                                            : [];

                                        const makeRow = (rowKey, rowMeta, isExtraSlot = false) => ({
                                          roleKey: rowKey,
                                          roleLabel: rowMeta?.roleLabel || rowKey,
                                          displayLabel: getWeeklyRoleLabel(rowMeta?.displayLabel || rowMeta?.roleLabel || rowKey),
                                          assignments: getAssignments(iso, rowKey),
                                          blocked: isBlocked(iso, rowKey),
                                          publishedShifts: getPublishedShiftsForSlot(iso, rowKey),
                                          isExtraSlot,
                                          onOpenAssign: () =>
                                            openAssignModalForSlot({
                                              iso,
                                              roleKey: rowKey,
                                              roleLabel: rowMeta?.roleLabel || rowKey,
                                              blockTitle: block.title,
                                              publishedPosition: "",
                                            }),
                                          onPublishedShiftClick: (shift) =>
                                            openAssignModalForPublishedShift({ ...shift, iso }, block.title),
                                          onEditAssignment: (assignment) => {
                                            setDetailsModal({
                                              iso,
                                              roleKey: assignment.roleKey || rowKey,
                                              assignmentId: assignment.assignmentId,
                                              employeeId: assignment.employeeId,
                                              roleLabel: rowMeta?.roleLabel || rowKey,
                                              start: assignment.start,
                                              end: assignment.end,
                                            });
                                          },
                                          onDropAssignment: (e) => handleDropOnSlot(e, iso, rowKey),
                                          onDragStart: (e, assignment) =>
                                            handleDragStart(e, {
                                              iso,
                                              roleKey: rowKey,
                                              assignmentId: assignment.assignmentId,
                                              employeeId: assignment.employeeId,
                                              start: assignment.start,
                                              end: assignment.end,
                                            }),
                                          onDragEnd: handleDragEnd,
                                          onDragEnter: () => setDragOverKey(`${iso}__${rowKey}`),
                                          onDragLeave: () =>
                                            setDragOverKey((cur) => (cur === `${iso}__${rowKey}` ? "" : cur)),
                                          onReorderExtraRow: isExtraSlot
                                            ? ({ sourceRoleKey, targetRoleKey, placement }) =>
                                                reorderInsideAssignmentExtraSlot({
                                                  iso,
                                                  blockTitle: block.title,
                                                  parentRoleKey: assignmentRoleKey,
                                                  sourceRoleKey,
                                                  targetRoleKey,
                                                  placement,
                                                })
                                            : undefined,
                                        });

                                        const isCartOrBike =
                                          /^Cart\s/i.test(roleLabel || "") || /^Bike\s/i.test(roleLabel || "");

                                        const rows = isCartOrBike
                                          ? [
                                              makeRow(
                                                `${assignmentRoleKey}__paramedic`,
                                                {
                                                  roleKey: `${assignmentRoleKey}__paramedic`,
                                                  roleLabel: "Paramedic",
                                                  displayLabel: "Paramedic",
                                                },
                                                false
                                              ),
                                              makeRow(
                                                `${assignmentRoleKey}__partner`,
                                                {
                                                  roleKey: `${assignmentRoleKey}__partner`,
                                                  roleLabel: "Partner",
                                                  displayLabel: "Partner",
                                                },
                                                false
                                              ),
                                              ...extraDefs.map((extra) =>
                                                makeRow(
                                                  extra.id,
                                                  {
                                                    roleKey: extra.id,
                                                    roleLabel: extra.label || extra.roleType || extra.id,
                                                    displayLabel: extra.label || extra.roleType || extra.id,
                                                  },
                                                  true
                                                )
                                              ),
                                            ]
                                            : isTentBase
                                            ? [1, 2, 3, 4].map((slotNum) => {
                                                const slotRoleKey = `${assignmentRoleKey}__${slotNum}`;
                                                const slotMeta = roleMetaByDate?.[iso]?.get(slotRoleKey) || {
                                                  roleKey: slotRoleKey,
                                                  roleLabel: assignmentRoleKey,
                                                  displayLabel: `${assignmentRoleKey} - Slot ${slotNum}`,
                                                  blockTitle: block.title,
                                                  isExtraSlot: false,
                                                };

                                                return makeRow(slotRoleKey, slotMeta, false);
                                              })
                                          : [
                                              makeRow(assignmentRoleKey, meta, false),
                                              ...extraDefs.map((extra) =>
                                                makeRow(
                                                  extra.id,
                                                  {
                                                    roleKey: extra.id,
                                                    roleLabel: extra.label || extra.roleType || extra.id,
                                                    displayLabel: extra.label || extra.roleType || extra.id,
                                                  },
                                                  true
                                                )
                                              ),
                                            ];

                                        return {
                                          roleKey: assignmentRoleKey,
                                          roleLabel,
                                          displayLabel: getWeeklyRoleLabel(meta?.displayLabel || roleLabel),
                                          rows,
                                        };
                                      }),
                                      assignmentsByRole: {},
                                      blockedSlots: {},
                                      publishedShiftsByRole: {},
                                      handlers: (rowKey) => {
                                        const rowMeta = roleMetaByDate?.[iso]?.get(rowKey) || {
                                          roleKey: rowKey,
                                          roleLabel: rowKey,
                                          blockTitle: block.title,
                                          isExtraSlot: false,
                                        };
                                        const rowRoleLabel = rowMeta?.roleLabel || rowKey;
                                        return makeRowHandlers(iso, rowKey, rowRoleLabel, block.title);
                                      },
                                    })}
                                  />
                                ) : block.title === "Back 9" ? (
                                  <Back9Card
                                    iso={iso}
                                    employeeById={employeeById}
                                    dragOverKey={dragOverKey}
                                    readOnly={!canEdit}
                                    groups={buildAssignmentGroups({
                                      groups: BACK_9_ASSIGNMENTS.map((assignmentRoleKey) => {
                                        const meta = roleMetaByDate?.[iso]?.get(assignmentRoleKey) || {
                                          roleKey: assignmentRoleKey,
                                          roleLabel: assignmentRoleKey,
                                          blockTitle: block.title,
                                          isExtraSlot: false,
                                        };

                                      const roleLabel = meta.roleLabel;
                                      const isTentBase = /^Tent\s+\d+$/i.test(String(assignmentRoleKey || ""));
                                      const extraDefs = canHaveInsideExtras(roleLabel)
                                        ? getInsideExtrasForRole(iso, block.title, assignmentRoleKey)
                                        : [];

                                        const makeRow = (rowKey, rowMeta, isExtraSlot = false) => ({
                                          roleKey: rowKey,
                                          roleLabel: rowMeta?.roleLabel || rowKey,
                                          displayLabel: getWeeklyRoleLabel(rowMeta?.displayLabel || rowMeta?.roleLabel || rowKey),
                                          assignments: getAssignments(iso, rowKey),
                                          blocked: isBlocked(iso, rowKey),
                                          publishedShifts: getPublishedShiftsForSlot(iso, rowKey),
                                          isExtraSlot,
                                          onOpenAssign: () =>
                                            openAssignModalForSlot({
                                              iso,
                                              roleKey: rowKey,
                                              roleLabel: rowMeta?.roleLabel || rowKey,
                                              blockTitle: block.title,
                                              publishedPosition: "",
                                            }),
                                          onPublishedShiftClick: (shift) =>
                                            openAssignModalForPublishedShift({ ...shift, iso }, block.title),
                                          onEditAssignment: (assignment) => {
                                            setDetailsModal({
                                              iso,
                                              roleKey: assignment.roleKey || rowKey,
                                              assignmentId: assignment.assignmentId,
                                              employeeId: assignment.employeeId,
                                              roleLabel: rowMeta?.roleLabel || rowKey,
                                              start: assignment.start,
                                              end: assignment.end,
                                            });
                                          },
                                          onDropAssignment: (e) => handleDropOnSlot(e, iso, rowKey),
                                          onDragStart: (e, assignment) =>
                                            handleDragStart(e, {
                                              iso,
                                              roleKey: rowKey,
                                              assignmentId: assignment.assignmentId,
                                              employeeId: assignment.employeeId,
                                              start: assignment.start,
                                              end: assignment.end,
                                            }),
                                          onDragEnd: handleDragEnd,
                                          onDragEnter: () => setDragOverKey(`${iso}__${rowKey}`),
                                          onDragLeave: () =>
                                            setDragOverKey((cur) => (cur === `${iso}__${rowKey}` ? "" : cur)),
                                          onReorderExtraRow: isExtraSlot
                                            ? ({ sourceRoleKey, targetRoleKey, placement }) =>
                                                reorderInsideAssignmentExtraSlot({
                                                  iso,
                                                  blockTitle: block.title,
                                                  parentRoleKey: assignmentRoleKey,
                                                  sourceRoleKey,
                                                  targetRoleKey,
                                                  placement,
                                                })
                                            : undefined,
                                        });

                                        const isCartOrBike =
                                          /^Cart\s/i.test(roleLabel || "") || /^Bike\s/i.test(roleLabel || "");

                                        const rows = isCartOrBike
                                          ? [
                                              makeRow(
                                                `${assignmentRoleKey}__paramedic`,
                                                {
                                                  roleKey: `${assignmentRoleKey}__paramedic`,
                                                  roleLabel: "Paramedic",
                                                  displayLabel: "Paramedic",
                                                },
                                                false
                                              ),
                                              makeRow(
                                                `${assignmentRoleKey}__partner`,
                                                {
                                                  roleKey: `${assignmentRoleKey}__partner`,
                                                  roleLabel: "Partner",
                                                  displayLabel: "Partner",
                                                },
                                                false
                                              ),
                                              ...extraDefs.map((extra) =>
                                                makeRow(
                                                  extra.id,
                                                  {
                                                    roleKey: extra.id,
                                                    roleLabel: extra.label || extra.roleType || extra.id,
                                                    displayLabel: extra.label || extra.roleType || extra.id,
                                                  },
                                                  true
                                                )
                                              ),
                                            ]
                                             : isTentBase
                                              ? [1, 2, 3, 4].map((slotNum) => {
                                                  const slotRoleKey = `${assignmentRoleKey}__${slotNum}`;
                                                  const slotMeta = roleMetaByDate?.[iso]?.get(slotRoleKey) || {
                                                    roleKey: slotRoleKey,
                                                    roleLabel: assignmentRoleKey,
                                                    displayLabel: `${assignmentRoleKey} - Slot ${slotNum}`,
                                                    blockTitle: block.title,
                                                    isExtraSlot: false,
                                                  };

                                                  return makeRow(slotRoleKey, slotMeta, false);
                                                })
                                          : [
                                              makeRow(assignmentRoleKey, meta, false),
                                              ...extraDefs.map((extra) =>
                                                makeRow(
                                                  extra.id,
                                                  {
                                                    roleKey: extra.id,
                                                    roleLabel: extra.label || extra.roleType || extra.id,
                                                    displayLabel: extra.label || extra.roleType || extra.id,
                                                  },
                                                  true
                                                )
                                              ),
                                            ];

                                        return {
                                          roleKey: assignmentRoleKey,
                                          roleLabel,
                                          displayLabel: getWeeklyRoleLabel(meta?.displayLabel || roleLabel),
                                          rows,
                                        };
                                      }),
                                      assignmentsByRole: {},
                                      blockedSlots: {},
                                      publishedShiftsByRole: {},
                                      handlers: (rowKey) => {
                                        const rowMeta = roleMetaByDate?.[iso]?.get(rowKey) || {
                                          roleKey: rowKey,
                                          roleLabel: rowKey,
                                          blockTitle: block.title,
                                          isExtraSlot: false,
                                        };
                                        const rowRoleLabel = rowMeta?.roleLabel || rowKey;
                                        return makeRowHandlers(iso, rowKey, rowRoleLabel, block.title);
                                      },
                                    })}
                                  />
                                ) : block.title === "Medical HQ" ? (
                                  <MedicalHQCard
                                    iso={iso}
                                    employeeById={employeeById}
                                    dragOverKey={dragOverKey}
                                    readOnly={!canEdit}
                                    onReorderExtraRow={({ sourceRoleKey, targetRoleKey, placement }) =>
                                      reorderMedicalHQExtraSlot({
                                        iso,
                                        sourceRoleKey,
                                        targetRoleKey,
                                        placement,
                                      })
                                    }
                                    rows={buildMedicalHQRows({
                                      rows: (() => {
                                        const medicalExtras = extras
                                          .filter((extra) => {
                                            const extraMeta = roleMetaByDate?.[iso]?.get(extra.id);
                                            return !!extraMeta;
                                          })
                                          .map((extra) => ({
                                            roleKey: extra.id,
                                            sortOrder: getMedicalHQExtraSortOrder(extra),
                                          }));

                                        const orderedRowKeys = [
                                          ...MEDICAL_HQ_BASE_ROWS.map((roleKey) => ({
                                            roleKey,
                                            sortOrder: getMedicalHQBaseOrder(roleKey),
                                          })),
                                          ...medicalExtras,
                                        ]
                                          .sort((a, b) => {
                                            if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
                                            const aIsBase = MEDICAL_HQ_BASE_ROWS.includes(a.roleKey);
                                            const bIsBase = MEDICAL_HQ_BASE_ROWS.includes(b.roleKey);
                                            if (aIsBase !== bIsBase) return aIsBase ? -1 : 1;
                                            return String(a.roleKey).localeCompare(String(b.roleKey));
                                          })
                                          .map((x) => x.roleKey);

                                        return orderedRowKeys.map((rowKey) => {
                                          const isBaseRole = MEDICAL_HQ_BASE_ROWS.includes(rowKey);

                                          const rowMeta = isBaseRole
                                            ? roleMetaByDate?.[iso]?.get(rowKey) || {
                                                roleKey: rowKey,
                                                roleLabel: rowKey,
                                                blockTitle: block.title,
                                                isExtraSlot: false,
                                              }
                                            : roleMetaByDate?.[iso]?.get(rowKey);

                                          const rowRoleLabel = getCanonicalRoleLabel(rowMeta?.roleLabel || rowKey);
                                          const rowDisplayLabel = formatRoleLabel(rowMeta?.displayLabel || rowMeta?.roleLabel || rowKey);
                                          const rowAssignments = getAssignments(iso, rowKey);

                                          return {
                                            roleKey: rowKey,
                                            roleLabel: rowRoleLabel,
                                            displayLabel: getWeeklyRoleLabel(rowDisplayLabel),
                                            assignment: rowAssignments[0] || null,
                                            assignments: rowAssignments,
                                            blocked: isBlocked(iso, rowKey),
                                            dragOver: dragOverKey === `${iso}__${rowKey}`,
                                            publishedShifts: getPublishedShiftsForSlot(iso, rowKey),
                                            unitText: getUnitText(iso, rowKey),
                                            isExtraSlot: !!rowMeta?.isExtraSlot,
                                            onUnitTextChange: (value) => setUnitText(iso, rowKey, value),
                                            onOpenAmbulanceEditor: () =>
                                              openAmbulanceModal({
                                                iso,
                                                roleKey: rowKey,
                                                roleLabel: rowRoleLabel,
                                                blockTitle: block.title,
                                              }),
                                          };
                                        });
                                      })(),
                                      assignmentsByRole: {},
                                      blockedSlots: {},
                                      publishedShiftsByRole: {},
                                      handlers: (rowKey) => {
                                        const rowMeta = roleMetaByDate?.[iso]?.get(rowKey) || {
                                          roleKey: rowKey,
                                          roleLabel: rowKey,
                                          blockTitle: block.title,
                                          isExtraSlot: false,
                                        };
                                        const rowRoleLabel = getCanonicalRoleLabel(rowMeta?.roleLabel || rowKey);
                                        const rowIsAmbulance = isAmbulanceSlot(rowRoleLabel);

                                        return {
                                          ...makeRowHandlers(iso, rowKey, rowRoleLabel, block.title),
                                          onOpenAssign: rowIsAmbulance
                                            ? undefined
                                            : () =>
                                                openSlotInteraction({
                                                  iso,
                                                  roleKey: rowKey,
                                                  roleLabel: rowRoleLabel,
                                                  blockTitle: block.title,
                                                  publishedPosition: "",
                                                }),
                                          onCardClick: () =>
                                            openSlotInteraction({
                                              iso,
                                              roleKey: rowKey,
                                              roleLabel: rowRoleLabel,
                                              blockTitle: block.title,
                                              publishedPosition: "",
                                            }),
                                          onOpen: () =>
                                            openSlotInteraction({
                                              iso,
                                              roleKey: rowKey,
                                              roleLabel: rowRoleLabel,
                                              blockTitle: block.title,
                                              publishedPosition: "",
                                            }),
                                          onClick: () =>
                                            openSlotInteraction({
                                              iso,
                                              roleKey: rowKey,
                                              roleLabel: rowRoleLabel,
                                              blockTitle: block.title,
                                              publishedPosition: "",
                                            }),
                                        };
                                      },
                                    })}
                                  />
                                ) : (
                                  <>
                                    {block.slots.map((roleKey) => {
                                      const meta = roleMetaByDate?.[iso]?.get(roleKey);
                                      if (!meta) return null;

                                      if (isTentSlot(meta.roleLabel)) {
                                        const insideParentExtras = getInsideParentTentExtras(iso, roleKey, meta.roleLabel);

                                        const extraSlots = insideParentExtras.map((extra) => ({
                                          id: extra.id,
                                          blocked: isBlocked(iso, extra.id),
                                          assignments: getAssignments(iso, extra.id),
                                          publishedShifts: getPublishedShiftsForSlot(iso, extra.id),

                                          onOpenAssign: () =>
                                            openAssignModalForSlot({
                                              iso,
                                              roleKey: extra.id,
                                              roleLabel: meta.roleLabel,
                                              blockTitle: meta.blockTitle,
                                              publishedPosition: "",
                                            }),

                                          onOpenPublishedShift: (shift) =>
                                            openAssignModalForPublishedShift({ ...shift, iso }, meta.blockTitle),

                                          onToggleBlocked: () => toggleBlocked(iso, extra.id),

                                          onDropAssignment: (e) => handleDropOnSlot(e, iso, extra.id),

                                          onDragEnter: () => setDragOverKey(`${iso}__${extra.id}`),

                                          onDragLeave: () =>
                                            setDragOverKey((cur) => (cur === `${iso}__${extra.id}` ? "" : cur)),
                                        }));

                                        return (
                                          <TentCard
                                            key={roleKey}
                                            iso={iso}
                                            roleKey={roleKey}
                                            roleLabel={meta.roleLabel}
                                            displayLabel={getWeeklyRoleLabel(meta.displayLabel || meta.roleLabel)}
                                            baseAssignments={getAssignments(iso, roleKey)}
                                            baseBlocked={isBlocked(iso, roleKey)}
                                            extraSlots={extraSlots}
                                            employeeById={employeeById}
                                            readOnly={!canEdit}
                                            dragOverKey={dragOverKey}
                                            getPublishedShiftsForSlot={getPublishedShiftsForSlot}
                                            onOpenAssign={() =>
                                              openAssignModalForSlot({
                                                iso,
                                                roleKey,
                                                roleLabel: meta.roleLabel,
                                                blockTitle: meta.blockTitle,
                                                publishedPosition: "",
                                              })
                                            }
                                            onOpenPublishedShift={(shift) =>
                                              openAssignModalForPublishedShift({ ...shift, iso }, meta.blockTitle)
                                            }
                                            onEditAssignment={(assignment) => {
                                              setDetailsModal({
                                                iso,
                                                roleKey: assignment.roleKey || roleKey,
                                                assignmentId: assignment.assignmentId,
                                                employeeId: assignment.employeeId,
                                                roleLabel: meta.roleLabel,
                                                start: assignment.start,
                                                end: assignment.end,
                                              });
                                            }}
                                            onToggleBlocked={() => toggleBlocked(iso, roleKey)}
                                            onAddExtraTentSlot={() => {
                                              addExtraSlot({
                                                iso,
                                                roleKey,
                                                blockTitle: meta.blockTitle,
                                                label: meta.roleLabel,
                                                insideParent: true,
                                              });
                                            }}
                                            onAddInsideCardSlot={() =>
                                              setAddPositionModal({
                                                iso,
                                                blockTitle: meta.blockTitle,
                                                roleKey,
                                                roleLabel: meta.roleLabel,
                                                options: getAddOptionsForBlock(meta.blockTitle),
                                                selected: getAddOptionsForBlock(meta.blockTitle)[0] || "",
                                              })
                                            }
                                            onRemoveExtra={(extraRoleKey) =>
                                              removeExtraSlot({ iso, blockTitle: meta.blockTitle, roleKey: extraRoleKey })
                                            }
                                            onDropAssignment={(e) => handleDropOnSlot(e, iso, roleKey)}
                                            onDragStart={(e, assignment) =>
                                              handleDragStart(e, {
                                                iso,
                                                roleKey,
                                                assignmentId: assignment.assignmentId,
                                                employeeId: assignment.employeeId,
                                                start: assignment.start,
                                                end: assignment.end,
                                              })
                                            }
                                            onDragEnd={handleDragEnd}
                                            onDragEnter={() => setDragOverKey(`${iso}__${roleKey}`)}
                                            onDragLeave={() =>
                                              setDragOverKey((cur) => (cur === `${iso}__${roleKey}` ? "" : cur))
                                            }
                                          />
                                        );
                                      }

                                      const handleCardClick = () =>
                                        openSlotInteraction({
                                          iso,
                                          roleKey,
                                          roleLabel: meta.roleLabel,
                                          blockTitle: meta.blockTitle,
                                          publishedPosition: "",
                                        });

                                      return (
                                        <SlotCard
                                          key={roleKey}
                                          iso={iso}
                                          roleKey={roleKey}
                                          roleLabel={meta.roleLabel}
                                          displayLabel={getWeeklyRoleLabel(meta.displayLabel || meta.roleLabel)}
                                          assignments={getAssignments(iso, roleKey)}
                                          blocked={isBlocked(iso, roleKey)}
                                          readOnly={!canEdit}
                                          employeeById={employeeById}
                                          dragOver={dragOverKey === `${iso}__${roleKey}`}
                                          unitText={getUnitText(iso, roleKey)}
                                          publishedShifts={getPublishedShiftsForSlot(iso, roleKey)}
                                          onAddInsideCardSlot={() =>
                                            setAddPositionModal({
                                              iso,
                                              blockTitle: meta.blockTitle,
                                              roleKey,
                                              roleLabel: meta.roleLabel,
                                              options: getAddOptionsForBlock(meta.blockTitle),
                                              selected: getAddOptionsForBlock(meta.blockTitle)[0] || "",
                                            })
                                          }
                                          onUnitTextChange={(value) => setUnitText(iso, roleKey, value)}
                                          onOpenAmbulanceEditor={() =>
                                            openAmbulanceModal({
                                              iso,
                                              roleKey,
                                              roleLabel: meta.roleLabel,
                                              blockTitle: meta.blockTitle,
                                            })
                                          }
                                          onCardClick={handleCardClick}
                                          onPublishedShiftClick={(shift) =>
                                            openAssignModalForPublishedShift({ ...shift, iso }, meta.blockTitle)
                                          }
                                          onEditAssignment={(assignment) => {
                                            setDetailsModal({
                                              iso,
                                              roleKey: assignment.roleKey || roleKey,
                                              assignmentId: assignment.assignmentId,
                                              employeeId: assignment.employeeId,
                                              roleLabel: meta.roleLabel,
                                              start: assignment.start,
                                              end: assignment.end,
                                            });
                                          }}
                                          onToggleBlocked={() => toggleBlocked(iso, roleKey)}
                                          onRemoveExtra={() => {}}
                                          onAddExtraTentSlot={() => {}}
                                          isExtraSlot={false}
                                          onDropAssignment={(e) => handleDropOnSlot(e, iso, roleKey)}
                                          onDropAssignmentOnCard={(e, targetAssignmentId, placement) =>
                                            handleDropOnAssignmentCard(e, iso, roleKey, targetAssignmentId, placement)
                                          }
                                          onDragStart={(e, assignment) =>
                                            handleDragStart(e, {
                                              iso,
                                              roleKey,
                                              assignmentId: assignment.assignmentId,
                                              employeeId: assignment.employeeId,
                                              start: assignment.start,
                                              end: assignment.end,
                                            })
                                          }
                                          onDragEnd={handleDragEnd}
                                          onDragEnter={() => setDragOverKey(`${iso}__${roleKey}`)}
                                          onDragLeave={() =>
                                            setDragOverKey((cur) => (cur === `${iso}__${roleKey}` ? "" : cur))
                                          }
                                        />
                                      );
                                    })}

                                    {extras.map((extra) => {
                                      const roleKey = extra.id;
                                      const meta = roleMetaByDate?.[iso]?.get(roleKey);
                                      if (!meta) return null;

                                      const handleCardClick = () =>
                                        openSlotInteraction({
                                          iso,
                                          roleKey,
                                          roleLabel: meta.roleLabel,
                                          blockTitle: meta.blockTitle,
                                          publishedPosition: "",
                                        });

                                      return (
                                        <SlotCard
                                          key={roleKey}
                                          iso={iso}
                                          roleKey={roleKey}
                                          roleLabel={meta.roleLabel}
                                          displayLabel={getWeeklyRoleLabel(meta.displayLabel || meta.roleLabel)}
                                          assignments={getAssignments(iso, roleKey)}
                                          blocked={isBlocked(iso, roleKey)}
                                          readOnly={!canEdit}
                                          employeeById={employeeById}
                                          dragOver={dragOverKey === `${iso}__${roleKey}`}
                                          unitText={getUnitText(iso, roleKey)}
                                          publishedShifts={getPublishedShiftsForSlot(iso, roleKey)}
                                          onAddInsideCardSlot={() =>
                                            setAddPositionModal({
                                              iso,
                                              blockTitle: meta.blockTitle,
                                              roleKey,
                                              roleLabel: meta.roleLabel,
                                              options: getAddOptionsForBlock(meta.blockTitle),
                                              selected: getAddOptionsForBlock(meta.blockTitle)[0] || "",
                                            })
                                          }
                                          onUnitTextChange={(value) => setUnitText(iso, roleKey, value)}
                                          onOpenAmbulanceEditor={() =>
                                            openAmbulanceModal({
                                              iso,
                                              roleKey,
                                              roleLabel: meta.roleLabel,
                                              blockTitle: meta.blockTitle,
                                            })
                                          }
                                          onCardClick={handleCardClick}
                                          onPublishedShiftClick={(shift) =>
                                            openAssignModalForPublishedShift({ ...shift, iso }, meta.blockTitle)
                                          }
                                          onEditAssignment={(assignment) => {
                                            setDetailsModal({
                                              iso,
                                              roleKey,
                                              assignmentId: assignment.assignmentId,
                                              employeeId: assignment.employeeId,
                                              roleLabel: meta.roleLabel,
                                              start: assignment.start,
                                              end: assignment.end,
                                            });
                                          }}
                                          onToggleBlocked={() => toggleBlocked(iso, roleKey)}
                                          onRemoveExtra={() => removeExtraSlot({ iso, blockTitle: block.title, roleKey })}
                                          onAddExtraTentSlot={() => {}}
                                          isExtraSlot={true}
                                          onDropAssignment={(e) => handleDropOnSlot(e, iso, roleKey)}
                                          onDropAssignmentOnCard={(e, targetAssignmentId, placement) =>
                                            handleDropOnAssignmentCard(e, iso, roleKey, targetAssignmentId, placement)
                                          }
                                          onDragStart={(e, assignment) =>
                                            handleDragStart(e, {
                                              iso,
                                              roleKey,
                                              assignmentId: assignment.assignmentId,
                                              employeeId: assignment.employeeId,
                                              start: assignment.start,
                                              end: assignment.end,
                                            })
                                          }
                                          onDragEnd={handleDragEnd}
                                          onDragEnter={() => setDragOverKey(`${iso}__${roleKey}`)}
                                          onDragLeave={() =>
                                            setDragOverKey((cur) => (cur === `${iso}__${roleKey}` ? "" : cur))
                                          }
                                        />
                                      );
                                    })}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={!!assignModal}
        title={assignModal ? `Manage Slot — ${assignModal.roleLabel} • ${prettyDate(assignModal.iso)}` : "Manage Slot"}
        onClose={() => setAssignModal(null)}
        footer={
          assignModal ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "grid", gap: 4 }}>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  Click a person to place them into this slot for the selected time range.
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className="btn btn-ghost"
                  disabled={!canEdit}
                  onClick={async () => {
                    if (!assignModalSlotState) return;

                    if (
                      !assignModalSlotState.blocked &&
                      (assignModalSlotState.hasAssignments || assignModalSlotState.hasUnitText)
                    ) {
                      alert("Remove the assignment or unit name before blocking this slot.");
                      return;
                    }

                    await toggleBlocked(assignModal.iso, assignModal.roleKey);
                  }}
                >
                  {assignModalSlotState?.blocked ? "Unblock Shift" : "Block Shift"}
                </button>

                {assignModalSlotState?.canRemoveSlot && (
                  <button
                    className="btn btn-ghost"
                    disabled={!canEdit}
                    onClick={async () => {
                      await removeExtraSlot({
                        iso: assignModal.iso,
                        blockTitle: assignModal.blockTitle,
                        roleKey: assignModal.roleKey,
                      });
                      setAssignModal(null);
                    }}
                  >
                    Remove Slot
                  </button>
                )}

                <button className="btn btn-ghost" onClick={() => setAssignModal(null)}>
                  Done
                </button>
              </div>
            </div>
          ) : null
        }
      >
        {assignModal && (
          <div style={{ display: "grid", gap: 10 }}>
            <div className="sched-modal-two-col">
              <div>
                <div className="sched-modal-label">Start</div>
                <input
                  type="time"
                  className="sched-modal-time"
                  value={assignModal.start}
                  onChange={(e) => setAssignModal((cur) => ({ ...cur, start: e.target.value }))}
                />
              </div>
              <div>
                <div className="sched-modal-label">End</div>
                <input
                  type="time"
                  className="sched-modal-time"
                  value={assignModal.end}
                  onChange={(e) => setAssignModal((cur) => ({ ...cur, end: e.target.value }))}
                />
              </div>
            </div>

            <input
              className="sched-modal-search"
              placeholder="Search name / email / position…"
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
            />

            <div className="sched-modal-list">
              {assignCandidates.length === 0 ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  No qualified employees are available for that day.
                </div>
              ) : (
                assignCandidates.map(({ emp, canAssign, statusLabel, reason, weekHours, availableForDay }) => {
                  const isNew = isNewStaff(emp);

                  return (
                    <button
                      key={emp.id}
                      className={`sched-modal-item ${isNew ? "sched-new-staff" : ""}`}
                      disabled={!canAssign}
                      onClick={async () => {
  if (!availableForDay) {
    const confirmed = window.confirm(
      `${getEmployeeName(emp)} is not marked available for this day.\n\nForce schedule anyway?`
    );

    if (!confirmed) return;
  }

  await assignEmployeeToSlot({
                          iso: assignModal.iso,
                          roleKey: assignModal.roleKey,
                          employeeId: emp.id,
                          start: assignModal.start,
                          end: assignModal.end,
                        });
                        setAssignModal(null);
                      }}
                      style={{
                        opacity: canAssign ? 1 : 0.55,
                        cursor: canAssign ? "pointer" : "not-allowed",
                        textAlign: "left",
                      }}
                      title={reason || ""}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div>
                          <div className="sched-modal-item-name" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span>{getEmployeeName(emp)}</span>

                            {isTentSlot(assignModal.roleLabel) && (
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  background: prefersAidStation(emp) ? "#dcfce7" : "#e2e8f0",
                                  color: prefersAidStation(emp) ? "#166534" : "#475569",
                                }}
                              >
                                {prefersAidStation(emp) ? "Preferred" : "Qualified"}
                              </span>
                            )}

                            {/^Bike\s/i.test(String(assignModal.roleLabel || "")) && (
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  background: "#dbeafe",
                                  color: "#1d4ed8",
                                }}
                              >
                                Bike Team
                              </span>
                            )}
                          </div>

                          <div className="sched-modal-item-sub">
                            {getEmployeePosition(emp) || "—"} • Scheduled: {weekHours.toFixed(1)} hrs
                          </div>
                        </div>

                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            color: canAssign ? "#166534" : "#64748b",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {statusLabel}
                        </div>
                      </div>
                      {!canAssign && reason ? (
                        <div style={{ marginTop: 4, fontSize: 11, color: "#64748b" }}>
                          {reason}
                        </div>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!ambulanceModal}
        title={
          ambulanceModal
            ? `Ambulance Unit — ${ambulanceModal.roleLabel} • ${prettyDate(ambulanceModal.iso)}`
            : "Ambulance Unit"
        }
        onClose={() => setAmbulanceModal(null)}
        footer={
          ambulanceModal ? (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setAmbulanceModal(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveAmbulanceUnit} disabled={!canEdit}>
                Save Unit
              </button>
            </div>
          ) : null
        }
      >
        {ambulanceModal && (
          <div style={{ display: "grid", gap: 10 }}>
            <div className="sched-modal-label">Unit Name</div>
            <input
              type="text"
              className="sched-modal-search"
              placeholder='Enter unit name (example: "Trinity 1")'
              value={ambulanceModal.value || ""}
              onChange={(e) =>
                setAmbulanceModal((cur) => ({
                  ...cur,
                  value: e.target.value,
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveAmbulanceUnit();
                }
              }}
              disabled={!canEdit}
              autoFocus
            />

            <div style={{ fontSize: 12, color: "#64748b" }}>
              Ambulance slots store a unit name instead of an employee assignment.
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!detailsModal}
        title={detailsModal ? `${detailsModal.roleLabel} • ${prettyDate(detailsModal.iso)}` : "Assignment"}
        onClose={() => setDetailsModal(null)}
      >
        {detailsModal && (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 950, fontSize: 16 }}>
              {getEmployeeName(employeeById.get(detailsModal.employeeId))}
              {getEmployeePosition(employeeById.get(detailsModal.employeeId))
                ? ` (${getEmployeePosition(employeeById.get(detailsModal.employeeId))})`
                : ""}
            </div>

            <div className="sched-modal-two-col">
              <div>
                <div className="sched-modal-label">Start</div>
                <input
                  type="time"
                  className="sched-modal-time"
                  value={detailsModal.start}
                  onChange={(e) => setDetailsModal((cur) => ({ ...cur, start: e.target.value }))}
                  disabled={!canEdit}
                />
              </div>
              <div>
                <div className="sched-modal-label">End</div>
                <input
                  type="time"
                  className="sched-modal-time"
                  value={detailsModal.end}
                  onChange={(e) => setDetailsModal((cur) => ({ ...cur, end: e.target.value }))}
                  disabled={!canEdit}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                className="btn btn-primary"
                disabled={!canEdit}
                onClick={async () => {
                  await updateAssignment({
                    iso: detailsModal.iso,
                    roleKey: detailsModal.roleKey,
                    assignmentId: detailsModal.assignmentId,
                    start: detailsModal.start,
                    end: detailsModal.end,
                  });
                  setDetailsModal(null);
                }}
              >
                Save Changes
              </button>

              <button
                className="btn btn-ghost"
                disabled={!canEdit}
                onClick={async () => {
                  await removeAssignment({
                    iso: detailsModal.iso,
                    roleKey: detailsModal.roleKey,
                    assignmentId: detailsModal.assignmentId,
                  });
                  setDetailsModal(null);
                }}
              >
                Remove from Slot
              </button>

              <button className="btn btn-ghost" onClick={() => setDetailsModal(null)}>
                Done
              </button>
            </div>

            <div style={{ color: "#64748b", fontSize: 12 }}>
              You can also drag this assignment card directly into another slot on the same day.
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!addPositionModal}
        title={
          addPositionModal
            ? `Add Position • ${addPositionModal.blockTitle} • ${prettyDate(addPositionModal.iso)}`
            : "Add Position"
        }
        onClose={() => setAddPositionModal(null)}
        footer={
          addPositionModal ? (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setAddPositionModal(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  const label = addPositionModal.selected;
                  if (!label) return;

                  await addExtraSlot({
                    iso: addPositionModal.iso,
                    blockTitle: addPositionModal.blockTitle,
                    label,
                    roleKey: addPositionModal.targetRoleKey || undefined,
                    insideParent: !!addPositionModal.targetRoleKey,
                  });

                  setAddPositionModal(null);
                }}
              >
                Add Position
              </button>
            </div>
          ) : null
        }
      >
        {addPositionModal && (
          <div style={{ display: "grid", gap: 10 }}>
            {Array.isArray(addPositionModal.targetOptions) &&
              addPositionModal.targetOptions.length > 0 && (
                <div>
                  <div className="sched-modal-label">Assignment</div>
                  <select
                    value={addPositionModal.targetRoleKey}
                    onChange={(e) =>
                      setAddPositionModal((cur) => ({
                        ...cur,
                        targetRoleKey: e.target.value,
                      }))
                    }
                    style={{
                      height: 40,
                      borderRadius: 10,
                      border: "1px solid #cbd5e1",
                      padding: "0 10px",
                      fontSize: 14,
                      background: "#fff",
                      width: "100%",
                      marginBottom: 10,
                    }}
                  >
                    {addPositionModal.targetOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {getWeeklyRoleLabel(opt)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            <div className="sched-modal-label">Select Position</div>

            <select
              value={addPositionModal.selected}
              onChange={(e) =>
                setAddPositionModal((cur) => ({
                  ...cur,
                  selected: e.target.value,
                }))
              }
              style={{
                height: 40,
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                padding: "0 10px",
                fontSize: 14,
                background: "#fff",
                width: "100%",
              }}
            >
              {addPositionModal.options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </Modal>

      <div className="print-header">Travelers Championship Medical — Daily Schedule</div>
      <div className="print-footer">Generated {new Date().toLocaleString()}</div>
    </div>
  );
}